/**
 * Normalize string for comparison: lowercase, trim, collapse spaces, remove accents.
 */
function normalize(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .replace(/[.,\-'_]/g, " ");
}

/**
 * Words of a normalized string (non-empty tokens).
 */
function words(normalized: string): string[] {
  return normalized.split(" ").filter((w) => w.length > 0);
}

/**
 * Score how well OCR text matches a company name (0 = no match, 1 = exact).
 * - Exact match: 1
 * - One contains the other: 0.9
 * - All OCR words appear in company (or vice versa): 0.7
 * - Some word overlap: proportional
 */
function matchScore(ocrNorm: string, companyNorm: string): number {
  if (ocrNorm === companyNorm) return 1;
  if (ocrNorm.length === 0 || companyNorm.length === 0) return 0;

  const ocrInCompany = companyNorm.includes(ocrNorm);
  const companyInOcr = ocrNorm.includes(companyNorm);
  if (ocrInCompany || companyInOcr) return 0.9;

  const ocrWords = words(ocrNorm);
  const companyWords = new Set(words(companyNorm));
  const matchCount = ocrWords.filter((w) => companyWords.has(w)).length;
  if (matchCount === ocrWords.length) return 0.7;
  if (matchCount > 0) return 0.4 * (matchCount / Math.max(ocrWords.length, companyWords.size));

  return 0;
}

/** Any object with at least a `name` property (e.g. Company). */
export type CompanyLike = { name: string };

const MIN_SCORE = 0.4;

/**
 * Find the best matching company from the list for the OCR-read string.
 * Returns the company object if score >= MIN_SCORE, else null.
 */
export function fuzzyMatchCompany<T extends CompanyLike>(
  ocrCompanyText: string,
  companies: T[]
): T | null {
  const trimmed = ocrCompanyText?.trim();
  if (!trimmed || companies.length === 0) return null;

  const ocrNorm = normalize(trimmed);
  let best: T | null = null;
  let bestScore = 0;

  for (const company of companies) {
    const name = company.name?.trim();
    if (!name) continue;
    const companyNorm = normalize(name);
    const score = matchScore(ocrNorm, companyNorm);
    if (score >= MIN_SCORE && score > bestScore) {
      bestScore = score;
      best = company;
    }
  }

  return best;
}
